import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { subscribe, checkCallsign } from "@/lib/api";

const reveal = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } },
};

const TypeCursor = () => (
  <span
    className="ml-0.5 inline-block h-3.5 w-2 -translate-y-px animate-blink bg-tactical-green align-middle"
    aria-hidden="true"
  />
);

function SuccessTerminal({ callsign, onExpire }) {
  const LINE1 = ">> ACCESS GRANTED · TRANSMITTER REGISTERED";
  const LINE2 = `Welcome to the Thinkavarium, ${callsign}. You're on the frequency — stand by, the next transmission reaches you the moment it drops.`;
  const [t1, setT1] = useState("");
  const [t2, setT2] = useState("");
  const [phase, setPhase] = useState("l1"); // l1 -> l2 -> done

  useEffect(() => {
    let cancelled = false;
    const timers = [];
    const speed = 33;
    const typeLine = (text, setter, next) => {
      let i = 0;
      const step = () => {
        if (cancelled) return;
        i += 1;
        setter(text.slice(0, i));
        if (i < text.length) timers.push(setTimeout(step, speed));
        else next();
      };
      timers.push(setTimeout(step, speed));
    };
    typeLine(LINE1, setT1, () => {
      if (cancelled) return;
      setPhase("l2");
      typeLine(LINE2, setT2, () => {
        if (cancelled) return;
        setPhase("done");
        timers.push(setTimeout(() => !cancelled && onExpire(), 15000));
      });
    });
    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="font-mono">
      <p className="text-sm tracking-[0.2em] text-tactical-green md:text-[15px]">
        {t1}
        {phase === "l1" && <TypeCursor />}
      </p>
      <p className="mt-2 min-h-[1.2em] text-xs font-light leading-relaxed text-white/70 md:text-[15px]">
        {t2}
        {phase === "l2" && <TypeCursor />}
      </p>
    </div>
  );
}

export default function Subscribe() {
  const [email, setEmail] = useState("");
  const [callsign, setCallsign] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [csStatus, setCsStatus] = useState(null); // null | checking | available | taken

  // Live callsign availability (debounced)
  useEffect(() => {
    const cs = callsign.trim();
    if (!cs) { setCsStatus(null); return; }
    setCsStatus("checking");
    const t = setTimeout(async () => {
      try {
        const available = await checkCallsign(cs);
        setCsStatus(available ? "available" : "taken");
      } catch {
        setCsStatus(null);
      }
    }, 450);
    return () => clearTimeout(t);
  }, [callsign]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    if (csStatus === "taken") {
      toast.error("That callsign is taken. Choose another.");
      return;
    }
    setLoading(true);
    try {
      const cs = callsign.trim();
      await subscribe(email, cs || null);
      setDone(true);
      setConfirmed(cs ? cs.toUpperCase() : "AGENT");
      setEmail("");
      setCallsign("");
      setCsStatus(null);
      setTimeout(() => setDone(false), 2500);
    } catch (err) {
      const msg = err?.response?.data?.detail || "TRANSMISSION FAILED. Retry.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="enlist" className="relative bg-bunker-shadow px-6 py-28 md:px-10 md:py-40" data-testid="subscribe">
      <div className="mx-auto max-w-3xl">
        <motion.div variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}>
          <div className="mb-3 flex items-center gap-3 font-mono text-xs tracking-[0.3em] text-reactor-red md:text-[15px]">
            <span className="hazard-stripes h-3 w-10" />
            MAINFRAME ACCESS TERMINAL
          </div>
          <h2 className="font-display text-5xl uppercase leading-[0.85] text-white md:text-8xl">
            Enlist Now
          </h2>
          <p className="mt-6 max-w-xl font-body text-sm font-light leading-relaxed text-white/60 md:text-xl">
            Register to receive every new episode the instant it&apos;s declassified.
            No spam, no noise - just the signal.
          </p>
        </motion.div>

        <motion.form
          variants={reveal}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          onSubmit={onSubmit}
          className="clip-hud mt-12 border border-hazard-amber/25 bg-void/70 p-6 backdrop-blur-md md:p-8"
          data-testid="subscribe-form"
        >
          <div className="mb-6 flex items-center justify-between font-mono text-[10px] tracking-widest text-white/40 md:text-[13px]">
            <span className="text-[13px] font-bold md:text-[16px]">ENLISTMENT APPLICATION</span>
            <span className="text-tactical-green">● SECURE</span>
          </div>

          <label className="mb-2 flex items-center justify-between font-mono text-[11px] uppercase tracking-[0.25em] text-hazard-amber md:text-[14px]">
            <span>Callsign <span className="text-white/30">(optional)</span></span>
            {csStatus === "checking" && <span className="text-white/40" data-testid="callsign-status">CHECKING...</span>}
            {csStatus === "available" && <span className="text-tactical-green" data-testid="callsign-status">● AVAILABLE</span>}
            {csStatus === "taken" && <span className="text-reactor-red" data-testid="callsign-status">✕ NOT AVAILABLE</span>}
          </label>
          <div className={`mb-6 flex items-center border-b pb-2 focus-within:border-hazard-amber ${csStatus === "taken" ? "border-reactor-red/60" : "border-white/15"}`}>
            <span className="mr-3 font-mono text-reactor-red md:text-lg">&gt;</span>
            <input
              value={callsign}
              onChange={(e) => setCallsign(e.target.value)}
              placeholder="AGENT_NAME"
              className="w-full bg-transparent font-mono text-white placeholder:text-white/25 focus:outline-none md:text-lg"
              data-testid="callsign-input"
            />
          </div>

          <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-hazard-amber md:text-[14px]">
            Email <span className="text-reactor-red">*</span>
          </label>
          <div className="mb-8 flex items-center border-b border-white/15 pb-2 focus-within:border-hazard-amber">
            <span className="mr-3 font-mono text-reactor-red md:text-lg">&gt;</span>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="agent@vault.io"
              className="w-full bg-transparent font-mono text-white placeholder:text-white/25 focus:outline-none md:text-lg"
              data-testid="email-input"
            />
            <span className="ml-1 inline-block h-4 w-2 animate-blink bg-hazard-amber" aria-hidden />
          </div>

          <button
            type="submit"
            disabled={loading || done}
            className="group relative w-full overflow-hidden border border-reactor-red bg-reactor-red px-8 py-4 font-mono text-xs font-bold tracking-[0.35em] text-void transition-colors duration-100 hover:bg-transparent hover:text-reactor-red disabled:opacity-60 md:text-[15px]"
            data-testid="subscribe-submit"
          >
            {loading ? "TRANSMITTING..." : done ? "► ACCESS GRANTED" : "► TRANSMIT & ENLIST"}
          </button>

          <div className="mt-5 flex items-center justify-between font-mono text-[10px] tracking-widest text-white/40 md:text-[13px]">
            <span>ENCRYPTED · AES-256</span>
            <span data-testid="connection-status" className="text-tactical-green">
              CONNECTED
            </span>
          </div>
        </motion.form>

        <AnimatePresence>
          {confirmed && (
            <motion.div
              initial={{ opacity: 0, y: 14, clipPath: "inset(0 0 100% 0)" }}
              animate={{ opacity: 1, y: 0, clipPath: "inset(0 0 0% 0)" }}
              exit={{ opacity: 0, y: 14, transition: { duration: 1.2, ease: "easeInOut" } }}
              transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
              className="clip-hud mt-4 flex items-start gap-4 border border-tactical-green/40 bg-tactical-green/[0.06] p-5 backdrop-blur-md md:p-6"
              data-testid="subscribe-success"
            >
              <span className="mt-1.5 h-2.5 w-2.5 shrink-0 animate-pulse bg-tactical-green" />
              <SuccessTerminal callsign={confirmed} onExpire={() => setConfirmed(null)} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
